import ProgressBar from "../UI/ProgressBar";
import {
  IconCheck,
  IconCalendar,
  IconCurrencyEuro,
  IconMapPin,
  IconReceipt,
  IconBuildingBank,
} from "@tabler/icons-react";
import { useState } from "react";
import moment from "moment/moment";
import Card from "../UI/Card";
import Status from "../UI/Status";
import Title from "../UI/Title";
import Text from "../UI/Text";
import Button from "../UI/Button";
import Accordion, { AccordionItem } from "../UI/Accordion";
import CollapsibleCard from "../UI/CollapsibleCard";
import InfoItem from "../UI/InfoItem";
import { Table } from "../UI/Table";
import FiscaliteStep, { type FiscaliteStepStatus } from "./FiscaliteStep";
import ComptaChecklist from "./ComptaChecklist";
import useComptaCompleteness from "../../hooks/use-compta-completeness";
import type { ComptaFinding } from "../../models/comptaFinding";
// @ts-expect-error pure js
import FiscaliteBienForm from "./FiscaliteBienForm.jsx";
// @ts-expect-error pure js
import ProfileBailleurForm from "../Profile/ProfileBailleurForm.jsx";
import DocumentShowList from "../File/DocumentShowList";
import type { Property } from "../../models/property";
import type { PropertyFolder } from "../../models/propertyFolder";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs.js";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties.js";
// @ts-expect-error pure js
import usePropertyFolders from "../../hooks/use-property-folders.js";
// @ts-expect-error pure js
import { useBalanceRecapProperties } from "../../hooks/use-balances.js";
// @ts-expect-error pure js
import { useModal } from "../../hooks/use-modal.jsx";
// @ts-expect-error pure js
import { useConfirm } from "../../hooks/use-confirm.jsx";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions.js";
// @ts-expect-error pure js
import { StatusFiscalDescription } from "../../models/profile.js";
import {
  PROPERTY_TYPE,
  FOLDER_TYPE,
  type Declaration,
  type FiscaliteItem,
  type FiscaliteItemType,
  type FiscaliteProfile,
} from "../../utils/fiscalite";
import styles from "./FiscaliteDeclaration.module.css";

type Recap = Record<string, number | string>;

interface RecapTableProps {
  recap?: Recap;
}

function RecapTable({ recap }: RecapTableProps) {
  const entries = Object.entries(recap ?? {});
  if (entries.length === 0) {
    return <Text className={styles.empty}>Aucune donnée pour ce bien.</Text>;
  }
  return (
    <Table variant="card">
      <Table.Header>
        <Table.Column>Éléments</Table.Column>
        <Table.Column align="right">Montant</Table.Column>
      </Table.Header>
      <Table.Body>
        {entries.map(([cle, valeur]) => {
          const isNeg = Number(valeur) < 0;
          return (
            <Table.Row key={cle}>
              <Table.Cell>
                <Text as="span" weight="bold">
                  {cle}
                </Text>
              </Table.Cell>
              <Table.Cell align="right">
                <Status
                  color={isNeg ? "danger" : "success"}
                  text={currencyFormatter(valeur, { withSign: true })}
                  variant="base"
                  weight="bold"
                ></Status>
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
}

interface BienAccordionProps {
  item: FiscaliteItem;
  type: FiscaliteItemType;
  year: number;
  recap?: Recap;
  findings?: ComptaFinding[];
  onEdit: () => void;
  onSendBien: () => void;
  onSendTransac: () => void;
}

function BienAccordion({
  item,
  type,
  year,
  recap,
  findings,
  onEdit,
  onSendBien,
  onSendTransac,
}: BienAccordionProps) {
  const sent = !!item.is_sent_to_compta;
  const transacSent = !!item.is_transac_sent_to_compta?.[year];
  const inProcess = (item.compta_payment_in_process || []).includes(year);
  const fullyDone = sent && transacSent;
  return (
    <AccordionItem
      value={`${type}-${item.id}`}
      variant="plain"
      title={
        <>
          <Text as="span" weight="bold">
            {item.nom}
          </Text>
          {fullyDone && (
            <Text
              as="span"
              color="success"
              background
              className={styles.bienAccordionDone}
            >
              <IconCheck size={14} stroke={3} />
            </Text>
          )}
        </>
      }
    >
      <div className={styles.accordionPanel}>
        <div className={styles.fields}>
          <InfoItem
            icon={IconCalendar}
            label="Date d'achat"
            value={
              item.date_achat
                ? moment(item.date_achat).format("DD/MM/YYYY")
                : "Non renseignée"
            }
          />
          <InfoItem
            icon={IconCurrencyEuro}
            label="Prix d'achat"
            value={
              item.prix_achat != null
                ? currencyFormatter(item.prix_achat)
                : "Non renseigné"
            }
          />
          <InfoItem
            icon={IconMapPin}
            label="Adresse"
            value={
              [item.rue, item.cp, item.ville].filter(Boolean).join(" ") ||
              "Non renseignée"
            }
          />
        </div>

        <DocumentShowList
          property={
            type === PROPERTY_TYPE ? (item as unknown as Property) : undefined
          }
          propertyFolder={
            type === FOLDER_TYPE
              ? (item as unknown as PropertyFolder)
              : undefined
          }
          allowEdit
          allowDelete
          fiscalite
        />

        {!sent && (
          <div className={styles.actions}>
            <Button variant="secondary" onClick={onEdit}>
              Modifier
            </Button>
            <Button onClick={onSendBien}>Valider et envoyer le bien</Button>
          </div>
        )}

        <CollapsibleCard
          variant="plain"
          className={styles.recapAccordion}
          title={
            <>
              <Text as="span" weight="bold">
                Récapitulatif fiscal
              </Text>
              {transacSent && (
                <Text
                  as="span"
                  color="success"
                  background
                  className={styles.bienAccordionDone}
                >
                  <IconCheck size={14} stroke={3} />
                </Text>
              )}
            </>
          }
        >
          <div className={styles.accordionPanel}>
            <ComptaChecklist findings={findings} />
            <RecapTable recap={recap} />
            {!transacSent && (
              <div className={styles.actions}>
                <Button disabled={inProcess} onClick={onSendTransac}>
                  Valider et envoyer les transactions
                </Button>
              </div>
            )}
          </div>
        </CollapsibleCard>
      </div>
    </AccordionItem>
  );
}

interface FiscaliteDeclarationProps {
  declaration: Declaration;
}

interface SendBienPayload {
  item: FiscaliteItem;
  type: FiscaliteItemType;
  isTransac: boolean;
}

export default function FiscaliteDeclaration({
  declaration,
}: FiscaliteDeclarationProps) {
  const { profileBailleurToQlower } = useProfileBailleurs({ withById: true });
  const { updateProperty, propertyToQlower, propertyTransacToQlower } =
    useProperties();
  const {
    updatePropertyFolder,
    propertyFolderToQlower,
    propertyFolderTransacToQlower,
  } = usePropertyFolders();
  const { recapProperties } = useBalanceRecapProperties();
  const { completeness } = useComptaCompleteness(declaration.year);

  const [profileBailleurToEdit, setProfileBailleurToEdit] =
    useState<FiscaliteProfile>();
  const [propertyToEdit, setPropertyToEdit] = useState<{
    prop: FiscaliteItem | null;
    type: FiscaliteItemType | null;
  }>({ prop: null, type: null });

  const [
    ProfileBailleurFormModal,
    closeProfileBailleurFormModal,
    openProfileBailleurFormModal,
  ] = useModal();
  const [
    FiscaliteBienFormModal,
    closeFiscaliteBienFormModal,
    openFiscaliteBienFormModal,
  ] = useModal();
  const [ConfirmSendProfile, openConfirmSendProfile] = useConfirm();
  const [ConfirmSendBien, openConfirmSendBien] = useConfirm();

  const {
    profile,
    year,
    properties: declProperties,
    folders: declFolders,
  } = declaration;

  const allItems: { item: FiscaliteItem; type: FiscaliteItemType }[] = [
    ...declProperties.map((p) => ({
      item: p,
      type: PROPERTY_TYPE as FiscaliteItemType,
    })),
    ...declFolders.map((f) => ({
      item: f,
      type: FOLDER_TYPE as FiscaliteItemType,
    })),
  ];

  async function handleSendProfile(profileTarget: FiscaliteProfile) {
    try {
      await profileBailleurToQlower(profileTarget.id);
    } catch (err) {
      handleAPIError(err);
    }
  }

  async function handleSendBien({ item, type, isTransac }: SendBienPayload) {
    try {
      if (type === FOLDER_TYPE) {
        if (isTransac) await propertyFolderTransacToQlower(item.id, year);
        else await propertyFolderToQlower(item.id);
      } else {
        if (isTransac) await propertyTransacToQlower(item.id, year);
        else await propertyToQlower(item.id);
      }
    } catch (err) {
      handleAPIError(err);
    }
  }

  const step1Status: FiscaliteStepStatus = declaration.profileSent
    ? "done"
    : "active";
  const step2Status: FiscaliteStepStatus =
    declaration.propertiesSent && declaration.transacSent
      ? "done"
      : declaration.profileSent
      ? "active"
      : "pending";

  const activeStep =
    step1Status === "active" ? 1 : step2Status === "active" ? 2 : null;

  return (
    <>
      <ProfileBailleurFormModal>
        <ProfileBailleurForm
          profileBailleur={profileBailleurToEdit}
          onSubmit={() => closeProfileBailleurFormModal()}
        />
      </ProfileBailleurFormModal>
      <FiscaliteBienFormModal>
        <FiscaliteBienForm
          property={propertyToEdit.prop}
          onSubmit={async (values: Record<string, unknown>) => {
            if (propertyToEdit.type === PROPERTY_TYPE && propertyToEdit.prop) {
              updateProperty(propertyToEdit.prop.id, values);
            } else if (
              propertyToEdit.type === FOLDER_TYPE &&
              propertyToEdit.prop
            ) {
              await updatePropertyFolder(propertyToEdit.prop.id, values);
            }
            closeFiscaliteBienFormModal();
          }}
        />
      </FiscaliteBienFormModal>
      <ConfirmSendProfile
        onAnswer={(ok: boolean, profileTarget: FiscaliteProfile) =>
          ok && handleSendProfile(profileTarget)
        }
        size="small"
      >
        <Text>
          Attention, une fois le profil envoyé, vous ne pourrez plus le
          modifier. Veuillez vous assurer que toutes les données entrées sont
          correctes.
        </Text>
      </ConfirmSendProfile>
      <ConfirmSendBien
        onAnswer={(ok: boolean, payload: SendBienPayload) =>
          ok && handleSendBien(payload)
        }
        size="small"
      >
        <Text>
          Attention, une fois les données envoyées, vous ne pourrez plus les
          modifier. Veuillez vous assurer que toutes les données entrées sont
          correctes.
        </Text>
      </ConfirmSendBien>

      <Card padding="none">
        <div className={styles.stateHeader}>
          <div className={styles.titleRow}>
            <Title as="h2" variant="deca" weight="bold">
              Déclaration fiscale - {profile.nom_profile}
            </Title>
            <Status color="info" text={`Année ${year}`} />
          </div>
          <ProgressBar
            value={declaration.tasksDone}
            max={declaration.tasksTotal}
          />
          <Text as="span" variant="deci" weight="medium" color="muted">
            Tâches à effectuer {declaration.tasksDone}/{declaration.tasksTotal}
          </Text>
        </div>

        <Accordion
          divider="border"
          defaultValue={activeStep ? [activeStep] : []}
        >
          <FiscaliteStep
            number={1}
            title="Information de l'entreprise"
            status={step1Status}
          >
            <div className={styles.accordionPanel}>
              <div className={styles.fields}>
                <InfoItem
                  icon={IconReceipt}
                  label="Régime Fiscal"
                  value={
                    profile.regime_fiscal
                      ? StatusFiscalDescription[profile.regime_fiscal]
                      : "Non renseigné"
                  }
                />
                <InfoItem
                  icon={IconBuildingBank}
                  label="Numéro de SIRET"
                  value={profile.siret || "Non renseigné"}
                />
                <InfoItem
                  icon={IconCalendar}
                  label="Début de l'activité comptable"
                  value={
                    profile.debut_activite
                      ? moment(profile.debut_activite).format("DD/MM/YYYY")
                      : "Non renseigné"
                  }
                />
              </div>
              <ComptaChecklist
                findings={completeness?.profiles?.[profile.id]}
              />
              {!declaration.profileSent && (
                <div className={styles.actions}>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setProfileBailleurToEdit(profile);
                      openProfileBailleurFormModal();
                    }}
                  >
                    Modifier
                  </Button>
                  <Button onClick={() => openConfirmSendProfile(profile)}>
                    Valider
                  </Button>
                </div>
              )}
            </div>
          </FiscaliteStep>

          <FiscaliteStep
            number={2}
            title="Informations des biens"
            helper={
              !declaration.profileSent
                ? "Vous devez valider l'encart précédent."
                : undefined
            }
            status={step2Status}
            disabled={!declaration.profileSent}
          >
            <Accordion divider="border">
              {allItems.map(({ item, type }) => {
                const recap = recapProperties[type]?.[item.id]?.[year];
                return (
                  <BienAccordion
                    key={`${type}-${item.id}`}
                    item={item}
                    type={type}
                    year={year}
                    recap={recap}
                    findings={completeness?.[type]?.[item.id]}
                    onEdit={() => {
                      setPropertyToEdit({ prop: item, type });
                      openFiscaliteBienFormModal();
                    }}
                    onSendBien={() =>
                      openConfirmSendBien({ item, type, isTransac: false })
                    }
                    onSendTransac={() =>
                      openConfirmSendBien({ item, type, isTransac: true })
                    }
                  />
                );
              })}
            </Accordion>
          </FiscaliteStep>
        </Accordion>
      </Card>
    </>
  );
}
